import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BuildingDataService } from '../services/buildingDataService';
import { LL97Service } from '../services/ll97Service';
import { RetrofitService } from '../services/retrofitService';
import { Building } from '../types/building';

interface CalculationVerificationPageProps {
  buildings: Building[];
  loading?: boolean;
  error?: string | null;
}

const CalculationVerificationPage: React.FC<CalculationVerificationPageProps> = ({ buildings, loading = false, error = null }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedBuildings, setSelectedBuildings] = useState<Building[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Building[]>([]);
  const [calculationData, setCalculationData] = useState<{
    ll97: Record<string, any>,
    retrofit: Record<string, any>,
    costBenefit: Record<string, any>
  } | null>(null);

  // Debug: Log buildings data
  useEffect(() => {
    console.log('CalculationVerificationPage - Buildings received:', buildings?.length || 0);
    console.log('Sample building:', buildings?.[0]);
    console.log('Loading:', loading);
    console.log('Error:', error);
  }, [buildings, loading, error]);

  // Debug state for UI
  const [debugInfo, setDebugInfo] = useState<string>('');

  useEffect(() => {
    const info = [
      `Loading: ${loading}`,
      `Error: ${error || 'None'}`,
      `Buildings array: ${buildings ? 'defined' : 'undefined'}`,
      `Buildings count: ${buildings?.length || 0}`,
      `Sample building: ${buildings?.[0] ? 'Available' : 'Not available'}`,
      `Sample building name: ${buildings?.[0]?.propertyName || 'N/A'}`
    ].join('\n');
    setDebugInfo(info);
  }, [buildings, loading, error]);

  // Filtering suggestions based on search query
  useEffect(() => {
    if (searchQuery.length > 2 && buildings && buildings.length > 0) {
      const matches = buildings
        .filter(b =>
          (b.propertyName && b.propertyName.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (b.address && b.address.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        .slice(0, 10);
      console.log('Search query:', searchQuery, 'Matches found:', matches.length);
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, buildings]);

  const handleBuildingSelect = (building: Building) => {
    if (selectedBuildings.length < 2) {
      if (!selectedBuildings.find(b => b.id === building.id)) {
        setSelectedBuildings([...selectedBuildings, building]);
      }
    }
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const removeBuilding = (building: Building) => {
    setSelectedBuildings(selectedBuildings.filter(b => b.id !== building.id));
  };

  const runCalculations = () => {
    if (selectedBuildings.length === 0) return;

    const calculationResults: {
      ll97: Record<string, any>,
      retrofit: Record<string, any>,
      costBenefit: Record<string, any>
    } = {
      ll97: {},
      retrofit: {},
      costBenefit: {}
    };

    selectedBuildings.forEach(building => {
      // LL97 Calculations
      const ll97Calculation = LL97Service.calculateLL97Fine(
        building.buildingType,
        building.grossFloorArea,
        building.totalGHGEmissions,
        2025
      );

      const ll97Calculation2030 = LL97Service.calculateLL97Fine(
        building.buildingType,
        building.grossFloorArea,
        building.totalGHGEmissions,
        2030
      );

      const emissionLimit = LL97Service.getEmissionLimit(building.buildingType, 2025);
      const emissionLimit2030 = LL97Service.getEmissionLimit(building.buildingType, 2030);

      const gapCalculation = LL97Service.calculateEmissionsGap(
        building.buildingType,
        building.grossFloorArea,
        building.totalGHGEmissions,
        2025
      );

      // Retrofit Option Calculations
      const retrofitOptions = RetrofitService.getApplicableRetrofitOptions(building);
      const costPerSqFt = 15; // baseline cost for retrofits

      // Create a standard retrofit package for this example
      const retrofitPackages = RetrofitService.generateRetrofitPackages(building);

      // Get the best retrofit package (highest ll97SavingsVsCost ratio)
      const bestPackage = retrofitPackages.length > 0 ? retrofitPackages[0] : null;

      const retrofitCalcs = {
        applicableOptions: retrofitOptions,
        packages: retrofitPackages,
        bestPackage: bestPackage
      };

      // Cost-Benefit Analysis
      const costBenefitCalcs = bestPackage ? {
        netPresentValue: bestPackage.netPresentValue,
        internalRateOfReturn: BuildingDataService.calculateIRR(
          bestPackage.totalCost,
          ll97Calculation.currentAnnualFine * (bestPackage.totalGHGReduction / 100),
          10
        ),
        paybackPeriod: bestPackage.combinedPaybackPeriod,
        ll97SavingsVsCost: bestPackage.ll97SavingsVsCost,
        projections: {
          year1: {
            savings: ll97Calculation.currentAnnualFine * (bestPackage.totalGHGReduction / 100),
            roi: (ll97Calculation.currentAnnualFine * (bestPackage.totalGHGReduction / 100)) / bestPackage.totalCost * 100
          },
          year5: {
            savings: ll97Calculation.currentAnnualFine * (bestPackage.totalGHGReduction / 100) * 5,
            roi: (ll97Calculation.currentAnnualFine * (bestPackage.totalGHGReduction / 100) * 5) / bestPackage.totalCost * 100
          },
          year10: {
            savings: ll97Calculation.cumulativeFines10Year * (bestPackage.totalGHGReduction / 100),
            roi: (ll97Calculation.cumulativeFines10Year * (bestPackage.totalGHGReduction / 100)) / bestPackage.totalCost * 100
          }
        }
      } : null;

      // Store all results
      calculationResults.ll97[building.id] = {
        basic: ll97Calculation,
        future: ll97Calculation2030,
        emissionLimit,
        emissionLimit2030,
        gapCalculation
      };

      calculationResults.retrofit[building.id] = retrofitCalcs;
      calculationResults.costBenefit[building.id] = costBenefitCalcs;
    });

    setCalculationData(calculationResults);
  };

  const formatNumber = (num: number | undefined, decimals = 0) => {
    if (num === undefined) return '-';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatCurrency = (num: number | undefined, decimals = 0) => {
    if (num === undefined) return '-';
    return `$${formatNumber(num, decimals)}`;
  };

  const formatPercentage = (num: number | undefined, decimals = 1) => {
    if (num === undefined) return '-';
    return `${formatNumber(num, decimals)}%`;
  };

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/tools"
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← Back to Tools
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">LL97 Calculation Verification</h1>
          <p className="text-lg text-gray-600">
            Step-by-step verification of all calculations for due diligence and compliance documentation.
          </p>
        </div>

        {/* Loading/Error States */}
        {loading || (!buildings || buildings.length === 0) ? (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="text-center py-8">
              {loading ? (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <svg className="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Building Data</h3>
                  <p className="text-gray-600 mb-4">
                    Please wait while we load the NYC building database...
                  </p>
                </>
              ) : error ? (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
                  <p className="text-gray-600 mb-4">
                    {error}
                  </p>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Building Data Available</h3>
                  <p className="text-gray-600 mb-4">
                    The building database appears to be empty or unavailable.
                  </p>
                </>
              )}

              <div className="text-sm text-gray-500 mb-4 space-y-1">
                <p>Loading: {loading ? 'Yes' : 'No'}</p>
                <p>Buildings loaded: {buildings?.length || 0}</p>
                <p>Error: {error || 'None'}</p>
              </div>

              <Link
                to="/tools"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Return to Tools
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Debug Information Panel */}
            <div className="bg-gray-100 border-l-4 border-gray-400 p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-800 mb-2">Debug Information</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <div>Loading: {loading ? 'Yes' : 'No'}</div>
                <div>Error: {error || 'None'}</div>
                <div>Buildings array: {buildings ? 'defined' : 'undefined'}</div>
                <div>Buildings count: {buildings?.length || 0}</div>
                <div>Sample building: {buildings?.[0] ? 'Available' : 'Not available'}</div>
                {buildings?.[0] && (
                  <div>Sample building name: {buildings[0].propertyName || 'N/A'}</div>
                )}
              </div>
            </div>

            {/* Building Selector */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Select Buildings to Verify (Max 2)</h2>

                {/* Debug info */}
                {buildings && buildings.length > 0 ? (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      ✅ {buildings.length.toLocaleString()} buildings loaded and available for search
                    </p>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-700">
                      ⚠️ No buildings data available. Buildings array: {buildings ? `${buildings.length} items` : 'undefined'}
                    </p>
                  </div>
                )}

                <div className="relative">
                  <input
                    type="text"
                    placeholder={buildings && buildings.length > 0
                      ? "Search for buildings by address or name..."
                      : "Loading building database..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={!buildings || buildings.length === 0}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {suggestions.map((building) => (
                        <div
                          key={building.id}
                          className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                          onClick={() => handleBuildingSelect(building)}
                        >
                          <div className="font-medium">{building.propertyName || 'Unnamed Building'}</div>
                          <div className="text-sm text-gray-500">{building.address}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Buildings */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Selected Buildings</h3>
                {selectedBuildings.length === 0 ? (
                  <p className="text-gray-500 italic">No buildings selected. Please search and select up to 2 buildings.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedBuildings.map((building) => (
                      <div key={building.id} className="border border-gray-300 rounded-lg p-4">
                        <div className="flex justify-between">
                          <div>
                            <h4 className="font-semibold">{building.propertyName || 'Unnamed Building'}</h4>
                            <p className="text-sm text-gray-600">{building.address}</p>
                            <div className="mt-1 text-xs">
                              <span className="text-gray-600">Type: </span>
                              <span className="font-medium">{building.buildingType}</span>
                              <span className="text-gray-600 ml-4">Size: </span>
                              <span className="font-medium">{formatNumber(building.grossFloorArea)} sq ft</span>
                            </div>
                          </div>
                          <button
                            onClick={() => removeBuilding(building)}
                            className="text-red-500 hover:text-red-700"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={runCalculations}
                  disabled={selectedBuildings.length === 0}
                  className={`px-4 py-2 rounded-md font-medium ${selectedBuildings.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Run Verification Calculations
                </button>
              </div>
            </div>

            {/* Calculation Results */}
            {calculationData && (
              <div className="space-y-8">
                {/* LL97 Verification */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    <span className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center text-blue-600 font-bold text-sm inline-block mr-3">1</span>
                    LL97 Emissions & Fine Calculations
                  </h2>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                          {selectedBuildings.map((building) => (
                            <th key={building.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {building.propertyName || building.address}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Building Type
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {building.buildingType}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Gross Floor Area (sq ft)
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(building.grossFloorArea)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Current Total GHG Emissions (tCO2e)
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(building.totalGHGEmissions, 1)}
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-blue-50">
                          <td colSpan={selectedBuildings.length + 1} className="px-6 py-3 text-left text-sm font-medium text-blue-800">
                            Step 1: Calculate LL97 Emission Limits
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2025 Emission Factor (tCO2e/sq ft)
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {calculationData.ll97[building.id].emissionLimit.toFixed(8)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2030 Emission Factor (tCO2e/sq ft)
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {calculationData.ll97[building.id].emissionLimit2030.toFixed(8)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2025 Emission Limit = Factor × Area (tCO2e)
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(calculationData.ll97[building.id].basic.applicableThreshold * building.grossFloorArea, 1)}
                              <div className="text-xs text-gray-400 mt-1">
                                {calculationData.ll97[building.id].emissionLimit.toFixed(5)} × {formatNumber(building.grossFloorArea)}
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2030 Emission Limit = Factor × Area (tCO2e)
                          </td>
                          {selectedBuildings.map((building) => (
                            <td key={building.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatNumber(calculationData.ll97[building.id].future.applicableThreshold * building.grossFloorArea, 1)}
                              <div className="text-xs text-gray-400 mt-1">
                                {calculationData.ll97[building.id].emissionLimit2030.toFixed(5)} × {formatNumber(building.grossFloorArea)}
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-blue-50">
                          <td colSpan={selectedBuildings.length + 1} className="px-6 py-3 text-left text-sm font-medium text-blue-800">
                            Step 2: Calculate LL97 Emissions Gap
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2025 Emissions Gap = Max(Current - Limit, 0) (tCO2e)
                          </td>
                          {selectedBuildings.map((building) => {
                            const gap = calculationData.ll97[building.id].gapCalculation.gap;
                            const textColor = gap > 0 ? 'text-red-600' : 'text-green-600';
                            return (
                              <td key={building.id} className={`px-6 py-4 whitespace-nowrap text-sm ${textColor}`}>
                                {formatNumber(gap, 1)}
                                <div className="text-xs text-gray-400 mt-1">
                                  Max({formatNumber(building.totalGHGEmissions, 1)} - {formatNumber(calculationData.ll97[building.id].basic.applicableThreshold * building.grossFloorArea, 1)}, 0)
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2025 Gap Percentage
                          </td>
                          {selectedBuildings.map((building) => {
                            const gapPct = calculationData.ll97[building.id].gapCalculation.gapPercentage;
                            const textColor = gapPct > 0 ? 'text-red-600' : 'text-green-600';
                            return (
                              <td key={building.id} className={`px-6 py-4 whitespace-nowrap text-sm ${textColor}`}>
                                {formatPercentage(gapPct)}
                              </td>
                            )
                          })}
                        </tr>
                        <tr className="bg-blue-50">
                          <td colSpan={selectedBuildings.length + 1} className="px-6 py-3 text-left text-sm font-medium text-blue-800">
                            Step 3: Calculate LL97 Fines
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2025 Annual Fine = Gap × $268 (USD)
                          </td>
                          {selectedBuildings.map((building) => {
                            const fine = calculationData.ll97[building.id].basic.currentAnnualFine;
                            const textColor = fine > 0 ? 'text-red-600 font-semibold' : 'text-green-600';
                            return (
                              <td key={building.id} className={`px-6 py-4 whitespace-nowrap text-sm ${textColor}`}>
                                {formatCurrency(fine)}
                                <div className="text-xs text-gray-400 mt-1">
                                  {formatNumber(calculationData.ll97[building.id].gapCalculation.gap, 1)} × $268
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2030 Annual Fine = Gap × $268 (USD)
                          </td>
                          {selectedBuildings.map((building) => {
                            const fine = calculationData.ll97[building.id].future.projected2030Fine;
                            const textColor = fine > 0 ? 'text-red-600 font-semibold' : 'text-green-600';
                            return (
                              <td key={building.id} className={`px-6 py-4 whitespace-nowrap text-sm ${textColor}`}>
                                {formatCurrency(fine)}
                              </td>
                            )
                          })}
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            10-Year Cumulative Fine (2025-2034)
                          </td>
                          {selectedBuildings.map((building) => {
                            const fine = calculationData.ll97[building.id].basic.cumulativeFines10Year;
                            const textColor = fine > 0 ? 'text-red-600 font-bold' : 'text-green-600';
                            return (
                              <td key={building.id} className={`px-6 py-4 whitespace-nowrap text-sm ${textColor}`}>
                                {formatCurrency(fine)}
                                <div className="text-xs text-gray-400 mt-1">
                                  ({formatCurrency(calculationData.ll97[building.id].basic.currentAnnualFine)} × 5) + ({formatCurrency(calculationData.ll97[building.id].future.projected2030Fine)} × 5)
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2">Important Notes on LL97 Calculations</h4>
                    <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1">
                      <li>Emission factors are sourced from NYC Local Law 97, 1 RCNY §103-14</li>
                      <li>The fine rate is set at $268 per metric ton of CO2e over allowed limit</li>
                      <li>Calculations assume no change in building emissions between periods</li>
                      <li>Allowed emissions decrease ~50-70% in 2030 depending on building type</li>
                      <li>Fine is $0 when building emissions are below the allowed limit</li>
                    </ul>
                  </div>
                </div>

                {/* Retrofit Verification */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    <span className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center text-blue-600 font-bold text-sm inline-block mr-3">2</span>
                    Retrofit Cost-Benefit Analysis
                  </h2>

                  <div className="space-y-8">
                    {selectedBuildings.map((building) => {
                      const retrofitData = calculationData.retrofit[building.id];
                      const costBenefitData = calculationData.costBenefit[building.id];

                      if (!retrofitData || !costBenefitData) return null;

                      return (
                        <div key={building.id} className="border border-gray-200 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{building.propertyName || building.address}</h3>

                          {retrofitData.bestPackage && (
                            <>
                              <div className="mb-4">
                                <h4 className="font-medium text-gray-800 mb-2">Selected Retrofit Package: {retrofitData.bestPackage.name}</h4>

                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calculation</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      <tr className="bg-blue-50">
                                        <td colSpan={3} className="px-4 py-2 text-left text-sm font-medium text-blue-800">
                                          Step 1: Calculate Retrofit Costs & Energy Reductions
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Total Retrofit Cost</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(retrofitData.bestPackage.totalCost)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Area × Cost/sq ft (varies by retrofit type)
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Energy Reduction</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatPercentage(retrofitData.bestPackage.totalEnergyReduction)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Combined reduction with diminishing returns calculation
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">GHG Emission Reduction</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatPercentage(retrofitData.bestPackage.totalGHGReduction)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Combined reduction with diminishing returns calculation
                                        </td>
                                      </tr>
                                      <tr className="bg-blue-50">
                                        <td colSpan={3} className="px-4 py-2 text-left text-sm font-medium text-blue-800">
                                          Step 2: Calculate Financial Benefits
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Annual LL97 Fine Savings</td>
                                        <td className="px-4 py-3 text-sm text-green-600">{formatCurrency(costBenefitData.projections.year1.savings)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Annual Fine × GHG Reduction %<br />
                                          {formatCurrency(calculationData.ll97[building.id].basic.currentAnnualFine)} × {formatPercentage(retrofitData.bestPackage.totalGHGReduction)} ÷ 100
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">10-Year LL97 Fine Savings</td>
                                        <td className="px-4 py-3 text-sm text-green-600 font-medium">{formatCurrency(costBenefitData.projections.year10.savings)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          10-Year Cumulative Fines × GHG Reduction %<br />
                                          {formatCurrency(calculationData.ll97[building.id].basic.cumulativeFines10Year)} × {formatPercentage(retrofitData.bestPackage.totalGHGReduction)} ÷ 100
                                        </td>
                                      </tr>
                                      <tr className="bg-blue-50">
                                        <td colSpan={3} className="px-4 py-2 text-left text-sm font-medium text-blue-800">
                                          Step 3: Calculate ROI Metrics
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Simple Payback Period</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{costBenefitData.paybackPeriod.toFixed(1)} years</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Initial Investment ÷ Annual Savings<br />
                                          {formatCurrency(retrofitData.bestPackage.totalCost)} ÷ {formatCurrency(costBenefitData.projections.year1.savings)}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Net Present Value (NPV)</td>
                                        <td className={`px-4 py-3 text-sm ${costBenefitData.netPresentValue > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatCurrency(costBenefitData.netPresentValue)}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Calculated at 7% discount rate over 10 years
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Internal Rate of Return (IRR)</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatPercentage(costBenefitData.internalRateOfReturn * 100)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          Iterative calculation based on Newton-Raphson method
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">LL97 Savings vs. Cost Ratio</td>
                                        <td className={`px-4 py-3 text-sm font-medium ${costBenefitData.ll97SavingsVsCost > 1 ? 'text-green-600' : 'text-red-600'}`}>
                                          {costBenefitData.ll97SavingsVsCost.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          10-Year Savings ÷ Initial Cost<br />
                                          {formatCurrency(costBenefitData.projections.year10.savings)} ÷ {formatCurrency(retrofitData.bestPackage.totalCost)}
                                        </td>
                                      </tr>
                                      <tr className="bg-blue-50">
                                        <td colSpan={3} className="px-4 py-2 text-left text-sm font-medium text-blue-800">
                                          Step 4: Calculate Year-by-Year ROI
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Year 1 ROI</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatPercentage(costBenefitData.projections.year1.roi)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          (Annual Savings ÷ Investment) × 100<br />
                                          ({formatCurrency(costBenefitData.projections.year1.savings)} ÷ {formatCurrency(retrofitData.bestPackage.totalCost)}) × 100
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Year 5 ROI</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatPercentage(costBenefitData.projections.year5.roi)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          (5-Year Savings ÷ Investment) × 100
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">Year 10 ROI</td>
                                        <td className="px-4 py-3 text-sm text-gray-700">{formatPercentage(costBenefitData.projections.year10.roi)}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          (10-Year Savings ÷ Investment) × 100
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <h4 className="font-medium text-gray-800 mb-2">Retrofit Package Components</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {retrofitData.bestPackage.retrofitOptions.map((option: any) => (
                                    <div key={option.id} className="bg-white p-3 border border-gray-200 rounded-lg">
                                      <h5 className="font-medium text-gray-800">{option.name}</h5>
                                      <p className="text-sm text-gray-600 mb-2">{option.description}</p>
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <span className="text-gray-500">Cost Per Sq Ft:</span> ${option.averageCostPerSqFt}
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Energy Reduction:</span> {option.averageEnergyReduction}%
                                        </div>
                                        <div>
                                          <span className="text-gray-500">GHG Reduction:</span> {option.averageGHGReduction}%
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Typical Payback:</span> {option.typicalPaybackPeriod} years
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}

                          {!retrofitData.bestPackage && (
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                              <p className="text-gray-500 italic">No retrofit package available for this building.</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2">Important Notes on Cost-Benefit Calculations</h4>
                    <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1">
                      <li>Retrofit costs are estimated based on NYC construction cost averages</li>
                      <li>Energy and GHG reductions include diminishing returns when multiple measures are combined</li>
                      <li>NPV calculations use a standard 7% discount rate over 10 years</li>
                      <li>IRR is calculated using the Newton-Raphson iterative method</li>
                      <li>Building-specific factors may affect actual costs and savings</li>
                    </ul>
                  </div>
                </div>

                {/* Methodology Section */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Calculation Methodology</h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">LL97 Fine Calculation</h3>
                      <div className="bg-gray-50 p-4 rounded-lg font-mono text-sm">
                        <div className="mb-1">1. Get emission factor for building type from NYC LL97</div>
                        <div className="mb-1">2. Calculate allowed emissions = factor × gross floor area</div>
                        <div className="mb-1">3. Calculate emissions gap = max(current emissions - allowed emissions, 0)</div>
                        <div className="mb-1">4. Calculate annual fine = gap × $268 per metric ton CO2e</div>
                        <div className="mb-1">5. Calculate 10-year fine = (2025-2029 annual fine × 5) + (2030-2034 annual fine × 5)</div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Retrofit Cost-Benefit Analysis</h3>
                      <div className="bg-gray-50 p-4 rounded-lg font-mono text-sm">
                        <div className="mb-1">1. Calculate retrofit cost = sum of (component cost/sq ft × building area)</div>
                        <div className="mb-1">2. Calculate energy savings = building energy use × % reduction</div>
                        <div className="mb-1">3. Calculate GHG reduction = building emissions × % reduction</div>
                        <div className="mb-1">4. Calculate annual LL97 fine savings = annual fine × % GHG reduction</div>
                        <div className="mb-1">5. Calculate payback period = retrofit cost ÷ annual savings</div>
                        <div className="mb-1">6. Calculate NPV using 7% discount rate over 10 years</div>
                        <div className="mb-1">7. Calculate IRR using Newton-Raphson method</div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Combined Measures Calculation</h3>
                      <div className="bg-gray-50 p-4 rounded-lg font-mono text-sm">
                        <div className="mb-1">For measures with reductions r₁, r₂, r₃, etc. (as percentages):</div>
                        <div className="mb-1">Combined reduction = r₁ + r₂×(1-r₁/100) + r₃×(1-r₁/100-r₂/100+r₁×r₂/10000) + ...</div>
                        <div className="mb-1">This accounts for diminishing returns when multiple measures target the same energy use.</div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">References & Citations</h3>
                      <ul className="list-disc pl-5 space-y-2">
                        <li className="text-sm text-gray-700">
                          <span className="font-medium">NYC Local Law 97 Emission Factors:</span> NYC Department of Buildings, 1 RCNY §103-14
                        </li>
                        <li className="text-sm text-gray-700">
                          <span className="font-medium">LL97 Fine Rate:</span> $268 per metric ton CO2e over limit
                        </li>
                        <li className="text-sm text-gray-700">
                          <span className="font-medium">Retrofit Cost Estimates:</span> Based on NYC construction cost averages from RSMeans and ASHRAE research
                        </li>
                        <li className="text-sm text-gray-700">
                          <span className="font-medium">Energy Savings Estimates:</span> Based on NYSERDA and DOE Building Performance Database
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Export Section */}
                <div className="bg-white rounded-lg shadow-md p-6 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Export Verification Report</h3>
                    <p className="text-sm text-gray-600">Generate a detailed PDF report for your records or due diligence.</p>
                  </div>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export PDF Report
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CalculationVerificationPage;
